import { Text, View, StyleSheet, Image, Font, pdf } from "@react-pdf/renderer";
import { useState } from "react";
import * as htmlToImage from "html-to-image";
import { Button } from "@core/button";
import { Download } from "lucide-react";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import italic from "@packages/assets/font-family/Montserrat-Italic.ttf";
import boldItalic from "@packages/assets/font-family/Montserrat-BoldItalic.ttf";
import firaCode from "@packages/assets/font-family/FiraCode-Regular.ttf";
import { useTranslation } from "react-i18next";
import PdfLayout from "@features/tools/components/pdf/PdfLayout";
import { useAppSelector } from "@packages/store/hooks";

Font.register({
  family: "Montserrat",
  fonts: [
    { src: regular, fontWeight: "normal" },
    { src: bold, fontWeight: "bold" },
    { src: italic, fontWeight: "normal", fontStyle: "italic" },
    { src: boldItalic, fontWeight: "bold", fontStyle: "italic" },
  ],
});

Font.register({
  family: "FiraCode",
  fonts: [{ src: firaCode, fontWeight: "normal" }],
});
interface ExportAllToPDFButtonProps {
  getRefs: () => HTMLDivElement[]; // refs of ai-content blocks
  slateContent: any[]; // full Slate editor content
  properties?: {
    title?: string;
    author?: string;
    imageUrl?: string;
    createdAt?: string;
  };
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Montserrat",
    padding: 20,
    flexDirection: "column",
    gap: 10,
  },
  paragraph: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  heading1: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 12,
  },
  heading2: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
  },
  listItem: {
    fontSize: 12,
    lineHeight: 1.5,
    marginLeft: 10,
    marginBottom: 4,
  },
  image: {
    width: "100%",
    marginBottom: 20,
  },
});

export const ExportAllToPDFButton = ({
  getRefs,
  slateContent,
  properties,
}: ExportAllToPDFButtonProps) => {
  const { t } = useTranslation();
  const customizationSettings = useAppSelector(
    (state) => state.user?.teacher?.preferences?.pdfOptions,
  );
  const [loading, setLoading] = useState(false);
  const generatePDFContent = async () => {
    setLoading(true);
    const refs = getRefs();
    const flatNodes = flattenNodes(slateContent);

    const result: (
      | { type: "text"; content: JSX.Element }
      | { type: "image"; src: string }
    )[] = [];
    let aiIndex = 0;

    for (const node of flatNodes) {
      if (node.type === "ai-content") {
        const ref = refs[aiIndex];
        if (ref) {
          const dataUrl = await htmlToImage.toPng(ref, {
            backgroundColor: "white",
            pixelRatio: 2,
            skipFonts: true,
          });
          result.push({ type: "image", src: dataUrl });
        }
        aiIndex++;
      } else if (
        [
          "paragraph",
          "heading-one",
          "heading-two",
          "bulleted-list",
          "numbered-list",
        ].includes(node.type)
      ) {
        result.push({
          type: "text",
          content: <SlateNodeToPDF node={node} />,
        });
      }
    }

    const PDFDoc = (
      // <Document>
      //   <Page size="A4" style={styles.page}>
      <PdfLayout
        author={properties?.author}
        headerTextColor="#0d9488"
        title={properties?.title}
        imageUrl={properties?.imageUrl}
        createdDate={properties?.createdAt}
        customizationSettings={customizationSettings}
        topic={properties?.title}
      >
        {result.map((block, i) =>
          block.type === "text" ? (
            <View key={i}>{block.content}</View>
          ) : (
            <Image key={i} src={block.src} style={styles.image} />
          ),
        )}
      </PdfLayout>
      //   </Page>
      // </Document>
    );
    const blob = await pdf(PDFDoc).toBlob();
    const url = URL.createObjectURL(blob);

    window.open(url, "_blank");

    setLoading(false);
  };

  return (
    <Button
      onClick={generatePDFContent}
      size="lg"
      loading={loading}
      bgColor="bg-purple-600 hover:bg-purple-700"
      rounded="full"
      textColor="text-white"
      className={`shadow-xl px-6 py-3 flex items-center gap-2 transition-transform duration-150 transform hover:scale-105 focus:scale-105 focus:ring-2 focus:ring-green-400/60 focus:ring-offset-2 ${loading ? "opacity-70 pointer-events-none cursor-wait" : ""}`}
    >
      <Download className="w-4 h-4" />
      {t("common.downloadPdf")}
    </Button>
  );
};

// Helper: Convert Slate node to PDF-compatible Text elements
function SlateNodeToPDF({ node }: { node: any }) {
  switch (node.type) {
    case "heading-one":
      return (
        <Text style={styles.heading1}>
          {node.children.map((c, i) => formatLeaf(c, i))}
        </Text>
      );

    case "heading-two":
      return (
        <Text style={styles.heading2}>
          {node.children.map((c, i) => formatLeaf(c, i))}
        </Text>
      );

    case "paragraph":
      return (
        <Text style={styles.paragraph}>
          {node.children.map((c, i) => formatLeaf(c, i))}
        </Text>
      );

    case "bulleted-list":
      return node.children.map((item: any, i: number) => (
        <Text key={i} style={styles.listItem}>
          • {item.children.map((c, j) => formatLeaf(c, j))}
        </Text>
      ));

    case "numbered-list": {
      let counter = 1;
      return node.children
        .filter((item: any) => item.type === "list-item")
        .map((item: any, i: number) => (
          <Text key={i} style={styles.listItem}>
            {`${counter++}. `}
            {item.children.map((c, j) => formatLeaf(c, j))}
          </Text>
        ));
    }
    case "list-item":
      return (
        <Text style={styles.listItem}>
          • {node.children.map((c, j) => formatLeaf(c, j))}
        </Text>
      );

    default:
      return null;
  }
}
function flattenNodes(nodes: any[], parentType: string | null = null): any[] {
  const result: any[] = [];

  const walk = (node: any, parentType: string | null) => {
    const isTopLevelListItem = node.type === "list-item" && parentType === null;
    const isRenderable =
      [
        "paragraph",
        "heading-one",
        "heading-two",
        "bulleted-list",
        "numbered-list",
        "ai-content",
      ].includes(node.type) || isTopLevelListItem;

    if (isRenderable) {
      result.push(node);
    }

    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => walk(child, node.type));
    }
  };

  nodes.forEach((node) => walk(node, parentType));
  return result;
}

// Helper: Format inline text leaf with styles
function formatLeaf(leaf: any, key: number) {
  const style: any = {};
  if (leaf.bold) style.fontWeight = "bold";
  if (leaf.italic) style.fontStyle = "italic";
  if (leaf.underline) style.textDecoration = "underline";
  if (leaf.size === "xl") style.fontSize = 16;

  return (
    <Text key={key} style={style}>
      {leaf.text}
    </Text>
  );
}
